import { assign, dispatch, query } from 'sdi/shape';
import {
    remoteError,
    remoteLoading,
    remoteSuccess,
    onRemoteNone,
    remoteNone,
    FeatureCollection,
} from 'sdi/source';
import {
    fetchIssueList,
    fetchIssueTypes,
    fetchOsmObject,
    Issue,
    IssueStatus,
    putIssue,
    IssueCriticality,
    fetchLinkedIssues,
    postTagList,
    TagForm,
    fetchEvaluationList,
    fetchIssueNoteList,
    IssueNote,
    putIssueNote,
    fetchGlobalNoteList,
    postIssueNote,
    GlobalNote,
    putGlobalNote,
    postGlobalNote,
    Evaluation,
    EvaluationList,
    putEvaluation,
    fetchOsmUser,
    postEvaluation,
    LinkedIssues,
    deleteIssueNote,
    deleteGlobalNote,
    fetchIssueGeometry,
    fetchCurrentOsmUser,
    postResolvingChangeset,
    fetchIssueGeometryCollection,
    fetchExternalIssue,
    fetchThemes,
    fetchIssueListByTheme,
} from '../remote';
import { fetchMapillaryImages } from '../remote/mapillary';
import debug = require('debug');
import {
    getIssueList,
    getPreviousOsmResourceId,
    OsmResourceId,
    osmResourceIdkey,
    getLinkedRemote,
    getIssueNoteList,
    getGlobalNote,
    getEvaluationList,
    getIssueOsmUserId,
    getGlobalNoteList,
    getCurrentOsmUid,
    getCurrentOsmUserName,
} from '../queries/qa';
import { fromNullable } from 'fp-ts/lib/Option';
import { updateCollection } from 'sdi/util';
import { updateIssueGeometryLayer } from './map';
import {
    clearRenderIssues,
    deselectRenderIssue,
    selectRenderIssue,
} from './app';
import { navigateClear } from './route';
import { getSelectedIssueIds, getSelectedIssues } from '../queries/table';
import { clearTagForm } from './osm';
import { clearTableTitle, setTableTitle, clearRowSelection } from './table';
import tr from 'sdi/locale';
import { scopeOption } from 'sdi/lib';

const logger = debug('sdi:events/qa');

export const loadTheme = () => {
    assign('qa/data/themes', remoteLoading);
    return fetchThemes()
        .then(themes => assign('qa/data/themes', remoteSuccess(themes)))
        .catch(err => assign('qa/data/themes', remoteError(err.toString())));
};

export const loadIssueList = () => {
    assign('qa/data/issues', remoteLoading);
    return fetchIssueList()
        .then(issues => assign('qa/data/issues', remoteSuccess(issues)))
        .catch(err => assign('qa/data/issues', remoteError(err.toString())));
};

export const loadIssueListByTheme = (theme: string) => {
    assign('qa/data/issues', remoteLoading);
    return fetchIssueListByTheme(theme)
        .then(issues => assign('qa/data/issues', remoteSuccess(issues)))
        .catch(err => assign('qa/data/issues', remoteError(err.toString())));
};

export const loadIssueTypes = () => {
    assign('qa/data/issue-types', remoteLoading);
    return fetchIssueTypes()
        .then(issueTypes =>
            assign('qa/data/issue-types', remoteSuccess(issueTypes))
        )
        .catch(err =>
            assign('qa/data/issue-types', remoteError(err.toString()))
        );
};

export const loadEvaluationList = () => {
    assign('qa/data/evaluations', remoteLoading);
    return fetchEvaluationList()
        .then(evalList =>
            assign('qa/data/evaluations', remoteSuccess(evalList))
        )
        .catch(err =>
            assign('qa/data/evaluations', remoteError(err.toString()))
        );
};

export const loadIssueNoteList = () => {
    assign('qa/data/notes/issue', remoteLoading);
    return fetchIssueNoteList()
        .then(issueNoteList =>
            assign('qa/data/notes/issue', remoteSuccess(issueNoteList))
        )
        .catch(err =>
            assign('qa/data/notes/issue', remoteError(err.toString()))
        );
};

export const loadGlobalNoteList = () => {
    assign('qa/data/notes/global', remoteLoading);
    return fetchGlobalNoteList()
        .then(globalNoteList =>
            assign('qa/data/notes/global', remoteSuccess(globalNoteList))
        )
        .catch(err =>
            assign('qa/data/notes/global', remoteError(err.toString()))
        );
};

export const loadIssueOsmUser = (userName: string) => {
    assign('qa/data/issue/user', remoteLoading);
    return fetchOsmUser(userName)
        .then(user =>
            fromNullable(user).foldL(
                () =>
                    assign(
                        'qa/data/issue/user',
                        remoteError('no user found for this user_name')
                    ),
                u => {
                    return assign('qa/data/issue/user', remoteSuccess(u));
                }
            )
        )
        .catch(err =>
            assign('qa/data/issue/user', remoteError(err.toString()))
        );
};

export const loadExternalIssue = (id: number) => {
    assign('qa/data/external-issue', remoteLoading);
    return fetchExternalIssue(id)
        .then(issue =>
            fromNullable(issue).foldL(
                () =>
                    assign(
                        'qa/data/external-issue',
                        remoteError('no external issue found')
                    ),
                i => {
                    return assign('qa/data/external-issue', remoteSuccess(i));
                }
            )
        )
        .catch(err =>
            assign('qa/data/external-issue', remoteError(err.toString()))
        );
};

export const clearExternalIssue = () =>
    assign('qa/data/external-issue', remoteNone);

export const loadCurrentOsmUser = () => {
    assign('qa/data/osm/user', remoteLoading);
    return fetchCurrentOsmUser()
        .then(user =>
            fromNullable(user).foldL(
                () =>
                    assign(
                        'qa/data/osm/user',
                        remoteError('you are not connected to OSM')
                    ),
                u => {
                    return assign('qa/data/osm/user', remoteSuccess(u));
                }
            )
        )
        .catch(err => assign('qa/data/osm/user', remoteError(err.toString())));
};

export const clearIssueOsmUser = () => assign('qa/data/issue/user', remoteNone);

// export const loadOsmObjectElement = (osmType: OsmType, osmId: number) => onNoneOsmObject(() => {
//     assign('qa/issues/select/element', remoteLoading);
//     fetchElements(osmType, osmId)
//         .then(elements => assign('qa/issues/select/element', remoteSuccess(elements.elements.tags)))
//         .catch(err => assign('qa/issues/select/element', remoteError(err.toString())))
// })

export const loadMapillary = (issueId: number, coord: [number, number]) => {
    fromNullable(query('qa/data/mapillary/images')[issueId.toString()]).foldL(
        () => {
            dispatch('qa/data/mapillary/images', c =>
                updateCollection(c, issueId.toString(), remoteLoading)
            );
            fetchMapillaryImages(coord[1], coord[0])
                .then(i =>
                    dispatch('qa/data/mapillary/images', s =>
                        updateCollection(
                            s,
                            issueId.toString(),
                            remoteSuccess(i)
                        )
                    )
                )
                .catch(err =>
                    dispatch('qa/data/mapillary/images', s =>
                        updateCollection(
                            s,
                            issueId.toString(),
                            remoteError(err)
                        )
                    )
                );
        },
        () => {}
    );
};

export const loadOsmObject = (osmId: OsmResourceId) =>
    fromNullable(
        query('qa/issues/select/osm-object')[osmResourceIdkey(osmId)]
    ).foldL(
        () => {
            dispatch('qa/issues/select/osm-object', s =>
                updateCollection(s, osmResourceIdkey(osmId), remoteLoading)
            );
            fetchOsmObject(osmId)
                .then(o => {
                    dispatch('qa/issues/select/osm-object', s =>
                        updateCollection(
                            s,
                            osmResourceIdkey(osmId),
                            remoteSuccess(o)
                        )
                    );
                })
                .catch(err =>
                    dispatch('qa/issues/select/osm-object', s =>
                        updateCollection(
                            s,
                            osmResourceIdkey(osmId),
                            remoteError(err.toString())
                        )
                    )
                );
        },
        () => {}
    );

export const loadPreviousOsmObject = (osmId: OsmResourceId) =>
    getPreviousOsmResourceId(osmId).map(id =>
        fromNullable(
            query('qa/issues/select/osm-object')[osmResourceIdkey(id)]
        ).foldL(
            () => {
                dispatch('qa/issues/select/osm-object', s =>
                    updateCollection(s, osmResourceIdkey(id), remoteLoading)
                );
                fetchOsmObject(id)
                    .then(o =>
                        dispatch('qa/issues/select/osm-object', s =>
                            updateCollection(
                                s,
                                osmResourceIdkey(id),
                                remoteSuccess(o)
                            )
                        )
                    )
                    .catch(err =>
                        dispatch('qa/issues/select/osm-object', s =>
                            updateCollection(
                                s,
                                osmResourceIdkey(id),
                                remoteError(err.toString())
                            )
                        )
                    );
            },
            () => {}
        )
    );

export const loadIssueGeometry = (issueId: number) => {
    fetchIssueGeometry(issueId)
        .then(g => {
            const fc = {
                current: {
                    type: 'FeatureCollection',
                    features: [g.current],
                } as FeatureCollection,
                previous: {
                    type: 'FeatureCollection',
                    features: [g.previous],
                } as FeatureCollection,
            };
            dispatch('qa/issue/geometries/collection', s =>
                updateCollection(s, issueId.toString(), fc)
            );
        })
        .catch(err => logger(`fetchIssueGeometry Error: ${err}`));
};

export const loadIssueGeometryCollection = (issueId: number) =>
    fetchIssueGeometryCollection(issueId)
        .then(gc =>
            dispatch('qa/issue/geometries/collection', s =>
                updateCollection(s, issueId.toString(), gc)
            )
        )
        .catch(err => logger(`fetchIssueGeometryCollection Error: ${err}`));

export const setLinkedIssuesRemote = (
    issueId: number,
    linkGroups: number[]
) => {
    const key = issueId.toString();
    dispatch('qa/issues/linked/remote', col =>
        updateCollection(col, key, remoteSuccess(linkGroups))
    );
};
export const removeLinkedIssuesRemote = (issueId: number) => {
    const key = issueId.toString();
    dispatch('qa/issues/linked/remote', col =>
        updateCollection(col, key, remoteNone)
    );
};

export const loadLinkedIssues = (
    id: number // issueId
) =>
    onRemoteNone(() => getLinkedRemote(id))(() => {
        const key = id.toString();
        dispatch('qa/issues/linked/remote', col =>
            updateCollection(col, key, remoteLoading)
        );
        fetchLinkedIssues(id)
            .then(ls => {
                const gIds = ls.map(g => g.id);
                setLinkedIssuesRemote(id, gIds);
                dispatch('qa/issues/linked', s =>
                    s.filter(g => gIds.indexOf(g.id) < 0).concat(ls)
                );
            })
            .catch(err =>
                dispatch('qa/issues/linked/remote', col =>
                    updateCollection(col, key, remoteError(`${err}`))
                )
            );
    });

const updateIssue = (id: number, newIssue: Issue) =>
    putIssue(id, newIssue)
        .then(data => {
            logger('data', data);
            const newIssueList = getIssueList().map(il =>
                il.filter(i => i.id !== id).concat(newIssue)
            );
            newIssueList.map(nil =>
                assign('qa/data/issues', remoteSuccess(nil))
            );
        })
        .catch(err => assign('qa/data/issues', remoteError(err.toString())));

const updateIssueNote = (id: number, newNote: IssueNote) =>
    putIssueNote(id, newNote)
        .then(data => {
            logger('data', data);
            const newIssueNoteList = getIssueNoteList().map(nl =>
                nl.filter(n => n.id !== id).concat(newNote)
            );
            newIssueNoteList.map(nl =>
                assign('qa/data/notes/issue', remoteSuccess(nl))
            );
        })
        .catch(err =>
            assign('qa/data/notes/issue', remoteError(err.toString()))
        );

const createIssueNote = (issueId: number, text: string) =>
    postIssueNote({ issue_id: issueId, text })
        .then(note => {
            getIssueNoteList()
                .map(list => list.concat(note))
                .map(newList =>
                    assign('qa/data/notes/issue', remoteSuccess(newList))
                );
        })
        .catch(err =>
            assign('qa/data/notes/issue', remoteError(err.toString()))
        );

const updateGlobalNote = (id: number, newNote: GlobalNote) =>
    putGlobalNote(id, newNote)
        .then(data => {
            logger('data', data);
            assign('qa/data/notes/global', remoteSuccess([newNote]));
        })
        .catch(err =>
            assign('qa/data/notes/global', remoteError(err.toString()))
        );

const createGlobalNote = (text: string) =>
    postGlobalNote({ text })
        .then(note => {
            assign('qa/data/notes/global', remoteSuccess([note]));
        })
        .catch(err =>
            assign('qa/data/notes/global', remoteError(err.toString()))
        );

const updateEvaluation = (userId: number, evaluation: EvaluationList) =>
    putEvaluation(userId, evaluation)
        .then(data => {
            logger('data', data);
            const newEvaluationList = getEvaluationList().map(el =>
                el.filter(e => e.user_uid !== userId).concat(evaluation)
            );
            newEvaluationList.map(el =>
                assign('qa/data/evaluations', remoteSuccess(el))
            );
        })
        .catch(err =>
            assign('qa/data/evaluations', remoteError(err.toString()))
        );

const createEvaluation = (evaluation: EvaluationList) =>
    postEvaluation(evaluation)
        .then(e =>
            getEvaluationList()
                .map(el => el.concat(e))
                .map(newList =>
                    assign('qa/data/evaluations', remoteSuccess(newList))
                )
        )
        .catch(err =>
            assign('qa/data/evaluations', remoteError(err.toString()))
        );

export const changeIssueNoteText = (issue: Issue, text: string) => {
    const noteList = getIssueNoteList().getOrElse([]);
    const oldNoteOpt = fromNullable(
        noteList.find(note => note.issue_id === issue.id)
    );
    oldNoteOpt.foldL(
        () => createIssueNote(issue.id, text),
        n =>
            updateIssueNote(n.id, {
                id: n.id,
                text,
                issue_id: n.issue_id,
            })
    );
};

export const deleteIssueNoteText = (issue: Issue) => {
    getIssueNoteList().map(list =>
        fromNullable(list.find(n => n.issue_id === issue.id)).map(n =>
            deleteIssueNote(n.id).then(() => {
                const newNoteList = getIssueNoteList().map(nl =>
                    nl.filter(n => n.issue_id !== issue.id)
                );
                newNoteList.map(nl =>
                    assign('qa/data/notes/issue', remoteSuccess(nl))
                );
            })
        )
    );
};

export const deleteGlobalNoteText = (_global: string) => {
    getGlobalNoteList().map(list =>
        fromNullable(list[0]).map(n =>
            deleteGlobalNote(n.id).then(() => {
                assign('qa/data/notes/global', remoteSuccess([]));
            })
        )
    );
};

export const changeGlobalNoteText = (_global: string, text: string) => {
    const oldNoteOpt = getGlobalNote();
    oldNoteOpt.foldL(
        () => createGlobalNote(text),
        n =>
            updateGlobalNote(n.id, {
                id: n.id,
                text,
            })
    );
};

export const changeEvaluation =
    (userName: string) => (evaluation: Evaluation) => {
        const list = getEvaluationList().getOrElse([]);
        const oldEval = fromNullable(list.find(e => e.user_name === userName));
        const createdEval = getIssueOsmUserId().map(id => ({
            user_uid: id,
            user_name: userName,
            evaluation,
        }));
        oldEval.foldL(
            () =>
                createdEval.foldL(
                    () => {},
                    e => createEvaluation(e)
                ),
            e =>
                updateEvaluation(e.user_uid, {
                    user_uid: e.user_uid,
                    user_name: userName,
                    evaluation,
                })
        );
    };

export const updateTags = (tagForm: TagForm) =>
    postTagList(tagForm).map(promise =>
        promise.then(data => {
            logger('data', data);
            clearTagForm();
        })
    );

export const changeStatus = (issue: Issue) => (newStatus: IssueStatus) => {
    const newIssue: Issue = {
        ...issue,
        status: newStatus,
    };
    updateIssue(issue.id, newIssue);
};
export const changeCriticality =
    (issue: Issue) => (newCriticality: IssueCriticality) => {
        const newIssue: Issue = {
            ...issue,
            criticity: newCriticality,
        };
        updateIssue(issue.id, newIssue);
    };

export const selectIssue = (id: number) => {
    dispatch('qa/issues/select', si => si.filter(s => s !== id).concat(id));
    updateIssueGeometryLayer();
    getSelectedIssues().length > 1
        ? clearRenderIssues()
        : selectRenderIssue(id);
};

export const deselectIssue = (issue: Issue) => {
    deselectRenderIssue(issue.id);
    dispatch('qa/issues/select', si => si.filter(s => s !== issue.id));
    clearRowSelection(issue.id);
    if (getSelectedIssueIds().length < 1) {
        navigateClear();
    }
};

export const clearSelectedIssues = () => assign('qa/issues/select', []);

export const selectLinkedGroup = (linkedGroup: LinkedIssues) => {
    assign('qa/issues/linked/selected', linkedGroup);
    setTableTitle(`${tr.qa('issues')} ${tr.qa(linkedGroup.link)}`);
};

export const deselectLinkedGroup = () => {
    assign('qa/issues/linked/selected', null);
    clearTableTitle();
};

export const createResolvingChangeset = (issue: Issue, changesetId: number) => {
    scopeOption()
        .let('user', getCurrentOsmUserName())
        .let(
            'uid',
            getCurrentOsmUid().chain(id => fromNullable(parseInt(id)))
        )
        .map(({ user, uid }) =>
            postResolvingChangeset({
                id: changesetId,
                user,
                uid,
                issues: [issue.id],
            }).catch(err => logger(err))
        );
};

export type GlobalFilter = 'newOrStalled' | 'new' | 'stalled' | 'allIssues';

export const setGlobalFilter = (filter: GlobalFilter) =>
    assign('qa/issues/filter/global', filter);
export const setFilterCriticality = (criticality: IssueCriticality) => {
    setTableTitle(`${tr.qa('filteredByCriticality')} : ${tr.qa(criticality)}`);
    assign('qa/issues/filter/criticality', criticality);
};

export const clearFilterCriticality = () =>
    assign('qa/issues/filter/criticality', null);

export const setRenderBaseLayerSelect = (status: boolean) =>
    assign('app/baselayer/select', status);

export const clearFilters = () => {
    deselectLinkedGroup();
    clearFilterCriticality();
    clearTableTitle();
};

logger('loaded');
